import { emptySplitApi as baseApi } from "./baseApi";

const api = baseApi.enhanceEndpoints({
  addTagTypes: [
    "SheqCatalogue",
    "SheqPpeRecord",
    "SheqIncident",
    "SheqInspection",
    "SheqDashboard",
    "SheqSop",
    "SheqHealthRecord",
  ] as const,
});

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SheqCatalogueItem {
  id?: string;
  code: string;
  name: string;
  description?: string;
  replacementCycleDays: number;
  requiresSize: boolean;
  availableSizes?: string; // JSON string
  riskCategory: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  legislativeRef?: string;
  isActive: boolean;
  /** When true this item is part of every worker's standard kit */
  isStandardIssue?: boolean;
  createdAt?: string;
}

export type PpeStatus = "ACTIVE" | "RETURNED" | "LOST" | "DAMAGED" | "EXPIRED";
export type PpeCondition = "NEW" | "GOOD" | "FAIR" | "POOR" | "CONDEMNED";

export interface SheqPpeRecord {
  id?: string;
  workerId: string;
  workerName: string;
  workerEmployeeNo?: string;
  department?: string;
  blockId?: string;
  blockName?: string;
  ppeItemId: string;
  ppeName: string;
  ppeCode?: string;
  size?: string;
  quantity: number;
  batchOrSerialNumber?: string;
  issuedDate: string;
  replacementDueDate: string;
  expectedReturnDate?: string;
  returnDate?: string;
  status: PpeStatus;
  condition: PpeCondition;
  returnCondition?: PpeCondition;
  returnNotes?: string;
  issuedBy?: string;
  issuedByName?: string;
  returnedToId?: string;
  staffAcknowledgedAt?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type IncidentType =
  | "NEAR_MISS"
  | "FIRST_AID"
  | "MEDICAL_TREATMENT"
  | "LOST_TIME"
  | "FATALITY"
  | "PROPERTY_DAMAGE"
  | "ENVIRONMENTAL"
  | "UNSAFE_ACT"
  | "UNSAFE_CONDITION";

export type IncidentSeverity = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
export type IncidentStatus = "OPEN" | "UNDER_INVESTIGATION" | "CLOSED";

export interface SheqIncident {
  id?: string;
  title: string;
  incidentDate: string;
  incidentTime?: string;
  blockId?: string;
  blockName?: string;
  location?: string;
  type: IncidentType;
  severity: IncidentSeverity;
  description: string;
  rootCause?: string;
  correctiveActions?: string;
  preventiveActions?: string;
  injuredPersonName?: string;
  injuredPersonRole?: string;
  bodyPartAffected?: string;
  medicalAttentionRequired: boolean;
  daysLost: number;
  reportedBy?: string;
  reportedByName?: string;
  witnessNames?: string;
  status: IncidentStatus;
  closedAt?: string;
  closedBy?: string;
  reportedToDoL: boolean;
  dolReferenceNumber?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type InspectionType =
  | "TOOLBOX_TALK"
  | "SITE_INSPECTION"
  | "PPE_CHECK"
  | "HOUSEKEEPING"
  | "FIRE_EQUIPMENT"
  | "ELECTRICAL"
  | "SCAFFOLD"
  | "LIFTING"
  | "GENERAL";

export type InspectionOutcome = "PASS" | "FAIL" | "CONDITIONAL";

export interface SheqInspection {
  id?: string;
  title: string;
  inspectionDate: string;
  blockId?: string;
  blockName?: string;
  type: InspectionType;
  outcome: InspectionOutcome;
  findings?: string;
  recommendations?: string;
  conductedBy?: string;
  conductedByName?: string;
  attendees?: string; // JSON string array
  attendeeCount: number;
  topicsCovered?: string;
  followUpStatus: "OPEN" | "CLOSED";
  followUpActions?: string;
  followUpDueDate?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SheqDashboard {
  ppe: {
    active: number;
    returned: number;
    expired: number;
    lost: number;
    damaged: number;
    expiringSoon: number;
  };
  incidents: { open: number; total: number };
  inspections: { total: number; failed: number };
  recentIncidents: SheqIncident[];
  expiringRecords: SheqPpeRecord[];
}

export interface SheqIncidentSummary {
  total: number;
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
  byStatus: Record<string, number>;
  totalDaysLost: number;
}

export type SopCategory =
  | "GENERAL"
  | "SAFETY"
  | "CONSTRUCTION"
  | "ELECTRICAL"
  | "PLUMBING"
  | "SCAFFOLDING"
  | "LIFTING"
  | "EXCAVATION"
  | "FINISHING"
  | "FIRE"
  | "ENVIRONMENT";

export type SopStatus = "DRAFT" | "ACTIVE" | "SUPERSEDED" | "ARCHIVED";

export interface SheqSop {
  id?: string;
  sopNumber: string;
  title: string;
  category: SopCategory;
  revision: string;
  effectiveDate: string;
  reviewDate?: string;
  status: SopStatus;
  purpose?: string;
  scope?: string;
  procedure?: string;
  hazards?: string;
  requiredPpe?: string; // JSON string array of PPE codes
  legislativeRef?: string;
  blockId?: string;
  blockName?: string;
  approvedBy?: string;
  approvedByName?: string;
  createdBy?: string;
  createdByName?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type HealthCheckType =
  | "PRE_EMPLOYMENT"
  | "PERIODIC"
  | "RETURN_TO_WORK"
  | "POST_INCIDENT"
  | "EXIT";

export type HealthRecordType = "HEALTH_CHECK" | "HEALTH_RECORD_UPDATE";

export type HealthFitStatus =
  | "FIT"
  | "FIT_WITH_RESTRICTIONS"
  | "TEMP_UNFIT"
  | "UNFIT"
  | "REFERRED";

export type HealthAccessLevel = "RESTRICTED" | "CONFIDENTIAL";
export type HealthRecordStatus = "ACTIVE" | "ARCHIVED" | "REDACTED";
export type HealthDocumentType =
  | "MEDICAL_CERTIFICATE"
  | "XRAY"
  | "SCAN"
  | "LAB_RESULTS"
  | "PRESCRIPTION"
  | "FIT_NOTE"
  | "OTHER";

export interface HealthDocument {
  documentType: HealthDocumentType;
  name: string;
  url: string;
  uploadedAt: string;
}

export interface SheqHealthRecord {
  id?: string;
  workerId: string;
  workerName: string;
  workerEmployeeNo?: string;
  department?: string;
  blockId?: string;
  blockName?: string;
  checkDate: string;
  checkType: HealthCheckType;
  recordType: HealthRecordType;
  providerName?: string;
  providerRegistrationNo?: string;
  fitStatus: HealthFitStatus;
  workRestrictions?: string;
  followUpRequired: boolean;
  followUpByDate?: string;
  nextReviewDate?: string;
  allergies?: string;
  chronicConditions?: string;
  currentMedication?: string;
  immunizations?: string;
  exposureNotes?: string;
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactPhone?: string;
  consentToStoreMedicalData: boolean;
  consentCapturedAt?: string;
  consentCapturedBy?: string;
  authorizedDisclosures?: string;
  accessLevel: HealthAccessLevel;
  retentionUntil?: string;
  recordStatus: HealthRecordStatus;
  supportingDocuments?: string; // JSON string array of HealthDocument
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

const sheqApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // ── Catalogue ─────────────────────────────────────────────────────────
    getSheqCatalogue: build.query<SheqCatalogueItem[], void>({
      query: () => "/sheq/ppe/catalogue",
      providesTags: [{ type: "SheqCatalogue", id: "LIST" }],
    }),
    createSheqCatalogueItem: build.mutation<
      SheqCatalogueItem,
      Omit<SheqCatalogueItem, "id" | "createdAt">
    >({
      query: (body) => ({ url: "/sheq/ppe/catalogue", method: "POST", body }),
      invalidatesTags: [{ type: "SheqCatalogue", id: "LIST" }],
    }),
    updateSheqCatalogueItem: build.mutation<
      SheqCatalogueItem,
      { id: string; body: Partial<SheqCatalogueItem> }
    >({
      query: ({ id, body }) => ({
        url: `/sheq/ppe/catalogue/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "SheqCatalogue", id: "LIST" }],
    }),
    deleteSheqCatalogueItem: build.mutation<void, string>({
      query: (id) => ({ url: `/sheq/ppe/catalogue/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "SheqCatalogue", id: "LIST" }],
    }),

    // ── PPE Register ──────────────────────────────────────────────────────
    getSheqPpeRecords: build.query<
      SheqPpeRecord[],
      {
        blockId?: string;
        status?: string;
        workerId?: string;
        from?: string;
        to?: string;
      }
    >({
      query: (params) => ({ url: "/sheq/ppe/records", params }),
      providesTags: [{ type: "SheqPpeRecord", id: "LIST" }],
    }),
    getSheqPpeRecord: build.query<SheqPpeRecord, string>({
      query: (id) => `/sheq/ppe/records/${id}`,
      providesTags: (_r, _e, id) => [{ type: "SheqPpeRecord", id }],
    }),
    // Active PPE holdings for a specific worker
    getSheqWorkerHoldings: build.query<SheqPpeRecord[], string>({
      query: (workerId) => `/sheq/ppe/worker/${workerId}/holdings`,
      providesTags: (_r, _e, workerId) => [
        { type: "SheqPpeRecord", id: `holdings-${workerId}` },
        { type: "SheqPpeRecord", id: "LIST" },
      ],
    }),
    issueSheqPpe: build.mutation<
      SheqPpeRecord,
      Omit<SheqPpeRecord, "id" | "createdAt" | "updatedAt"> & {
        overrideExisting?: boolean;
      }
    >({
      query: (body) => ({ url: "/sheq/ppe/records", method: "POST", body }),
      invalidatesTags: [
        { type: "SheqPpeRecord", id: "LIST" },
        { type: "SheqDashboard", id: "MAIN" },
      ],
    }),
    updateSheqPpeRecord: build.mutation<
      SheqPpeRecord,
      { id: string; body: Partial<SheqPpeRecord> }
    >({
      query: ({ id, body }) => ({
        url: `/sheq/ppe/records/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SheqPpeRecord", id },
        { type: "SheqPpeRecord", id: "LIST" },
        { type: "SheqDashboard", id: "MAIN" },
      ],
    }),
    returnSheqPpe: build.mutation<
      SheqPpeRecord,
      {
        id: string;
        returnDate?: string;
        returnCondition?: string;
        returnNotes?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/sheq/ppe/records/${id}/return`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "SheqPpeRecord", id: "LIST" },
        { type: "SheqDashboard", id: "MAIN" },
      ],
    }),
    deleteSheqPpeRecord: build.mutation<void, string>({
      query: (id) => ({ url: `/sheq/ppe/records/${id}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "SheqPpeRecord", id: "LIST" },
        { type: "SheqDashboard", id: "MAIN" },
      ],
    }),

    // ── Dashboard ─────────────────────────────────────────────────────────
    getSheqDashboard: build.query<SheqDashboard, { blockId?: string }>({
      query: (params) => ({ url: "/sheq/dashboard", params }),
      providesTags: [{ type: "SheqDashboard", id: "MAIN" }],
    }),

    // ── Incidents ─────────────────────────────────────────────────────────
    getSheqIncidents: build.query<
      SheqIncident[],
      {
        blockId?: string;
        status?: string;
        type?: string;
        from?: string;
        to?: string;
      }
    >({
      query: (params) => ({ url: "/sheq/incidents", params }),
      providesTags: [{ type: "SheqIncident", id: "LIST" }],
    }),
    getSheqIncident: build.query<SheqIncident, string>({
      query: (id) => `/sheq/incidents/${id}`,
      providesTags: (_r, _e, id) => [{ type: "SheqIncident", id }],
    }),
    createSheqIncident: build.mutation<
      SheqIncident,
      Omit<SheqIncident, "id" | "createdAt" | "updatedAt">
    >({
      query: (body) => ({ url: "/sheq/incidents", method: "POST", body }),
      invalidatesTags: [
        { type: "SheqIncident", id: "LIST" },
        { type: "SheqDashboard", id: "MAIN" },
      ],
    }),
    updateSheqIncident: build.mutation<
      SheqIncident,
      { id: string; body: Partial<SheqIncident> }
    >({
      query: ({ id, body }) => ({
        url: `/sheq/incidents/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SheqIncident", id },
        { type: "SheqIncident", id: "LIST" },
        { type: "SheqDashboard", id: "MAIN" },
      ],
    }),
    deleteSheqIncident: build.mutation<void, string>({
      query: (id) => ({ url: `/sheq/incidents/${id}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "SheqIncident", id: "LIST" },
        { type: "SheqDashboard", id: "MAIN" },
      ],
    }),

    // ── Inspections ───────────────────────────────────────────────────────
    getSheqInspections: build.query<
      SheqInspection[],
      {
        blockId?: string;
        type?: string;
        outcome?: string;
        from?: string;
        to?: string;
      }
    >({
      query: (params) => ({ url: "/sheq/inspections", params }),
      providesTags: [{ type: "SheqInspection", id: "LIST" }],
    }),
    getSheqInspection: build.query<SheqInspection, string>({
      query: (id) => `/sheq/inspections/${id}`,
      providesTags: (_r, _e, id) => [{ type: "SheqInspection", id }],
    }),
    createSheqInspection: build.mutation<
      SheqInspection,
      Omit<SheqInspection, "id" | "createdAt" | "updatedAt">
    >({
      query: (body) => ({ url: "/sheq/inspections", method: "POST", body }),
      invalidatesTags: [
        { type: "SheqInspection", id: "LIST" },
        { type: "SheqDashboard", id: "MAIN" },
      ],
    }),
    updateSheqInspection: build.mutation<
      SheqInspection,
      { id: string; body: Partial<SheqInspection> }
    >({
      query: ({ id, body }) => ({
        url: `/sheq/inspections/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SheqInspection", id },
        { type: "SheqInspection", id: "LIST" },
      ],
    }),
    deleteSheqInspection: build.mutation<void, string>({
      query: (id) => ({ url: `/sheq/inspections/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "SheqInspection", id: "LIST" }],
    }),

    // ── Health Records ─────────────────────────────────────────────────────
    getSheqHealthRecords: build.query<
      SheqHealthRecord[],
      {
        blockId?: string;
        workerId?: string;
        checkType?: string;
        fitStatus?: string;
        from?: string;
        to?: string;
        followUpRequired?: boolean;
      }
    >({
      query: (params) => ({ url: "/sheq/health/records", params }),
      providesTags: [{ type: "SheqHealthRecord", id: "LIST" }],
    }),
    getSheqHealthRecord: build.query<SheqHealthRecord, string>({
      query: (id) => `/sheq/health/records/${id}`,
      providesTags: (_r, _e, id) => [{ type: "SheqHealthRecord", id }],
    }),
    createSheqHealthRecord: build.mutation<
      SheqHealthRecord,
      Omit<SheqHealthRecord, "id" | "createdAt" | "updatedAt">
    >({
      query: (body) => ({ url: "/sheq/health/records", method: "POST", body }),
      invalidatesTags: [{ type: "SheqHealthRecord", id: "LIST" }],
    }),
    updateSheqHealthRecord: build.mutation<
      SheqHealthRecord,
      { id: string; body: Partial<SheqHealthRecord> }
    >({
      query: ({ id, body }) => ({
        url: `/sheq/health/records/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SheqHealthRecord", id },
        { type: "SheqHealthRecord", id: "LIST" },
      ],
    }),
    deleteSheqHealthRecord: build.mutation<void, string>({
      query: (id) => ({ url: `/sheq/health/records/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "SheqHealthRecord", id: "LIST" }],
    }),

    // ── Reports ───────────────────────────────────────────────────────────
    getSheqExpiring: build.query<
      SheqPpeRecord[],
      { days?: number; blockId?: string }
    >({
      query: (params) => ({ url: "/sheq/reports/expiring", params }),
      providesTags: [{ type: "SheqPpeRecord", id: "LIST" }],
    }),
    getSheqIncidentSummary: build.query<
      SheqIncidentSummary,
      { from?: string; to?: string; blockId?: string }
    >({
      query: (params) => ({ url: "/sheq/reports/incidents-summary", params }),
      providesTags: [{ type: "SheqIncident", id: "LIST" }],
    }),

    // ── SOPs ──────────────────────────────────────────────────────────────
    getSheqSops: build.query<
      SheqSop[],
      { category?: string; status?: string; blockId?: string }
    >({
      query: (params) => ({ url: "/sheq/sops", params }),
      providesTags: [{ type: "SheqSop", id: "LIST" }],
    }),
    getSheqSop: build.query<SheqSop, string>({
      query: (id) => `/sheq/sops/${id}`,
      providesTags: (_r, _e, id) => [{ type: "SheqSop", id }],
    }),
    createSheqSop: build.mutation<
      SheqSop,
      Omit<SheqSop, "id" | "createdAt" | "updatedAt">
    >({
      query: (body) => ({ url: "/sheq/sops", method: "POST", body }),
      invalidatesTags: [{ type: "SheqSop", id: "LIST" }],
    }),
    updateSheqSop: build.mutation<
      SheqSop,
      { id: string; body: Partial<SheqSop> }
    >({
      query: ({ id, body }) => ({
        url: `/sheq/sops/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SheqSop", id },
        { type: "SheqSop", id: "LIST" },
      ],
    }),
    deleteSheqSop: build.mutation<void, string>({
      query: (id) => ({ url: `/sheq/sops/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "SheqSop", id: "LIST" }],
    }),
  }),
});

export const {
  useGetSheqCatalogueQuery,
  useCreateSheqCatalogueItemMutation,
  useUpdateSheqCatalogueItemMutation,
  useDeleteSheqCatalogueItemMutation,
  useGetSheqPpeRecordsQuery,
  useGetSheqPpeRecordQuery,
  useGetSheqWorkerHoldingsQuery,
  useIssueSheqPpeMutation,
  useUpdateSheqPpeRecordMutation,
  useReturnSheqPpeMutation,
  useDeleteSheqPpeRecordMutation,
  useGetSheqDashboardQuery,
  useGetSheqIncidentsQuery,
  useGetSheqIncidentQuery,
  useCreateSheqIncidentMutation,
  useUpdateSheqIncidentMutation,
  useDeleteSheqIncidentMutation,
  useGetSheqInspectionsQuery,
  useGetSheqInspectionQuery,
  useCreateSheqInspectionMutation,
  useUpdateSheqInspectionMutation,
  useDeleteSheqInspectionMutation,
  useGetSheqHealthRecordsQuery,
  useGetSheqHealthRecordQuery,
  useCreateSheqHealthRecordMutation,
  useUpdateSheqHealthRecordMutation,
  useDeleteSheqHealthRecordMutation,
  useGetSheqExpiringQuery,
  useGetSheqIncidentSummaryQuery,
  useGetSheqSopsQuery,
  useGetSheqSopQuery,
  useCreateSheqSopMutation,
  useUpdateSheqSopMutation,
  useDeleteSheqSopMutation,
} = sheqApi;

export default sheqApi;
